import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Castle, Landmark, LibraryBig, Hospital, GraduationCap, Siren, Axe, Anchor, Church } from 'lucide-react';
import { Reveal } from './Reveal';
import { LOCATIONS, IMAGES, API } from '../data/content';

const ICONS = {
  castle: Castle,
  landmark: Landmark,
  library: LibraryBig,
  hospital: Hospital,
  graduation: GraduationCap,
  siren: Siren,
  axe: Axe,
  anchor: Anchor,
  church: Church,
};

const STONES = [
  { d: 'M24 20.8 v-2.6 q0 -1 1 -1 q1 0 1 1 v2.6 Z', x: 25, y: 17.2, name: 'Octavian León', dates: '1712 – 1798', audio: 'octavian' },
  { d: 'M27.4 19.6 v-2.4 q0 -0.9 0.9 -0.9 q0.9 0 0.9 0.9 v2.4 Z', x: 28.3, y: 16.3, name: 'Maebhe León', dates: '1688 – 1751', audio: 'maebhe' },
  { d: 'M30.6 20.4 v-2.6 q0 -1 1 -1 q1 0 1 1 v2.6 Z', x: 31.6, y: 16.8, name: 'Henriette León', dates: '1740 – 1802', audio: 'henriette' },
  { d: 'M36.4 19.8 v-2.4 q0 -0.9 0.9 -0.9 q0.9 0 0.9 0.9 v2.4 Z', x: 37.3, y: 16.5, name: 'Solène León', dates: '1766 – 1829', audio: 'solene' },
  { d: 'M39 20.6 v-3.4 M37.9 18.6 h2.2', cross: true, x: 39, y: 17.2, name: 'The Founder', dates: '1699 – 1842', audio: 'founder' },
];

const MapInk = ({ onWhisper }) => (
  <svg viewBox="0 0 100 75" preserveAspectRatio="none" className="absolute inset-0 w-full h-full" aria-hidden="true">
    {/* survey grid */}
    {[10, 20, 30, 40, 50, 60, 70, 80, 90].map((x) => (
      <line key={`v${x}`} x1={x} y1="0" x2={x} y2="75" stroke="#3a2f23" strokeWidth="0.12" opacity="0.14" />
    ))}
    {[15, 30, 45, 60].map((y) => (
      <line key={`h${y}`} x1="0" y1={y} x2="100" y2={y} stroke="#3a2f23" strokeWidth="0.12" opacity="0.14" />
    ))}

    {/* river */}
    <path d="M-2 8 C 20 14, 30 26, 22 40 C 15 52, 34 62, 44 77" stroke="#6b8299" strokeWidth="1.7" fill="none" opacity="0.55" />
    <path d="M-2 10.5 C 20 16.5, 27 27, 19.5 40 C 13 51.5, 32 62.5, 41.5 77" stroke="#6b8299" strokeWidth="0.5" fill="none" opacity="0.45" />

    {/* roads */}
    <g stroke="#3a2f23" strokeWidth="0.4" strokeDasharray="1.6 1.2" fill="none" opacity="0.75">
      <path d="M50 18 Q44 34 48 55" />
      <path d="M48 55 Q38 52 30 45" />
      <path d="M48 55 Q60 50 70 42" />
      <path d="M30 45 Q26 58 25 70" />
      <path d="M70 42 Q74 56 75 68" />
      <path d="M48 55 Q51 68 52 82" />
    </g>

    {/* bluff hatching near the mansion */}
    <g stroke="#3a2f23" strokeWidth="0.3" fill="none" opacity="0.7">
      <path d="M36 11 q3 -3.5 6 0" />
      <path d="M45 8 q3 -3.5 6 0" />
      <path d="M55 10 q3 -3.5 6 0" />
      <path d="M40 15 q3 -3.5 6 0" />
      <path d="M58 15 q3 -3.5 6 0" />
    </g>

    {/* the old highway, away to Sydney */}
    <path d="M48 55 Q64 59 78 62.5 Q84 64 90 63" stroke="#3a2f23" strokeWidth="0.55" fill="none" opacity="0.85" />
    <path d="M48 56.4 Q64 60.4 78 64 Q84 65.4 90 64.4" stroke="#3a2f23" strokeWidth="0.25" fill="none" opacity="0.6" />

    {/* township streets */}
    <g stroke="#3a2f23" strokeWidth="0.3" fill="none" opacity="0.8">
      <path d="M36 46 H58" />
      <path d="M38 50 H56" />
      <path d="M42 41 V53" />
      <path d="M50 41 V53" />
      <path d="M46 41 V53" strokeWidth="0.18" />
    </g>

    {/* township blocks */}
    <g stroke="#3a2f23" strokeWidth="0.28" fill="#3a2f23" fillOpacity="0.08" opacity="0.9">
      <rect x="37" y="42.2" width="2.6" height="1.9" />
      <rect x="43" y="42.4" width="2.2" height="1.7" />
      <rect x="51" y="42.2" width="2.8" height="2" />
      <rect x="37.4" y="47" width="2.2" height="1.8" />
      <rect x="43.2" y="47.2" width="2" height="1.6" />
      <rect x="51.4" y="47" width="2.4" height="1.8" />
      <rect x="39" y="50.8" width="2.4" height="1.7" />
      <rect x="51.2" y="50.6" width="2.6" height="1.9" />
    </g>
    {/* church spire, township square */}
    <path d="M46.6 47.8 V45.6 M46 47.8 h1.2 M46.6 45.6 l-0.5 1 h1 Z" stroke="#3a2f23" strokeWidth="0.3" fill="#3a2f23" />
    <text x="36.5" y="41.4" fontSize="2" fontFamily="Cormorant Garamond, serif" fontStyle="italic" fill="#3a2f23" opacity="0.85">the township</text>

    {/* Sydney, in the distance */}
    <g stroke="#3a2f23" opacity="0.9">
      {/* skyline ticks */}
      <g strokeWidth="0.45">
        <line x1="80.5" y1="66" x2="80.5" y2="62.8" />
        <line x1="82" y1="66" x2="82" y2="61.4" />
        <line x1="83.4" y1="66" x2="83.4" y2="63.4" />
        <line x1="93" y1="66" x2="93" y2="62.2" />
        <line x1="94.4" y1="66" x2="94.4" y2="63.6" />
        <line x1="95.7" y1="66" x2="95.7" y2="64.2" />
      </g>
      {/* harbour waterline */}
      <path d="M79 66.2 H97" strokeWidth="0.2" strokeDasharray="0.8 0.8" fill="none" opacity="0.7" />
      {/* Harbour Bridge */}
      <g transform="translate(88 63.4) scale(0.62)" fill="none">
        <path d="M-7 0 H7" strokeWidth="0.9" />
        <path d="M-7 0 Q0 -6.4 7 0" strokeWidth="0.9" />
        <path d="M-5 -1.85 V0 M-3 -3.3 V0 M0 -3.95 V0 M3 -3.3 V0 M5 -1.85 V0" strokeWidth="0.4" />
        <path d="M-6.6 0 v2 M6.6 0 v2" strokeWidth="1.3" />
      </g>
      <text x="88" y="70" fontSize="2.6" textAnchor="middle" fontFamily="Cormorant Garamond, serif" fontStyle="italic" fill="#3a2f23" stroke="none">SYDNEY</text>
      <text x="88" y="72.4" fontSize="1.5" textAnchor="middle" fontFamily="JetBrains Mono, monospace" letterSpacing="0.35" fill="#3a2f23" stroke="none" opacity="0.75">HARBOUR BRIDGE</text>
    </g>

    {/* road to the graveyard */}
    <path d="M30 34 Q31 28 33 24" stroke="#3a2f23" strokeWidth="0.4" strokeDasharray="1.6 1.2" fill="none" opacity="0.75" />

    {/* graveyard ground, whispering stones and dead tree */}
    <g stroke="#3a2f23" opacity="0.85">
      <path d="M22 21.5 Q31 17.5 40 21.5" fill="none" strokeWidth="0.35" />
      {STONES.map((s) => (
        <g key={s.name}>
          <path
            d={s.d}
            className={s.cross ? 'tombstone-cross' : 'tombstone'}
            fill={s.cross ? 'none' : '#3a2f23'}
            fillOpacity={s.cross ? undefined : 0.12}
            strokeWidth={s.cross ? 0.4 : 0.3}
          />
          <rect
            x={s.x - 1.7}
            y={s.y - 2.2}
            width="3.4"
            height="4.6"
            fill="transparent"
            stroke="none"
            className="cursor-pointer"
            style={{ pointerEvents: 'all' }}
            onMouseEnter={() => onWhisper(s)}
            onMouseLeave={() => onWhisper(null)}
            onClick={() => onWhisper(s)}
          />
        </g>
      ))}
      {/* dead tree */}
      <path d="M21.5 21 v-4.5 M21.5 18.5 l-2 -2 M21.5 17.5 l1.8 -2.2 M21.5 19.6 l2.4 -1" fill="none" strokeWidth="0.35" />
    </g>

    {/* double survey border */}
    <rect x="1.2" y="1.2" width="97.6" height="72.6" fill="none" stroke="#3a2f23" strokeWidth="0.5" opacity="0.8" />
    <rect x="2.6" y="2.6" width="94.8" height="69.8" fill="none" stroke="#3a2f23" strokeWidth="0.2" opacity="0.8" />

    {/* compass rose */}
    <g transform="translate(88 12)" stroke="#3a2f23">
      <circle r="5" fill="none" strokeWidth="0.35" />
      <circle r="3.4" fill="none" strokeWidth="0.18" opacity="0.7" />
      <path d="M0 -5 L1.1 0 L0 5 L-1.1 0 Z" fill="#3a2f23" stroke="none" />
      <path d="M-5 0 H5" strokeWidth="0.18" />
      <text y="-6.6" fontSize="3.2" textAnchor="middle" fill="#3a2f23" stroke="none" fontFamily="Cormorant Garamond, serif" fontStyle="italic">N</text>
    </g>
  </svg>
);

export const TownMap = ({ locations = LOCATIONS }) => {
  const [whisper, setWhisper] = useState(null);
  const audioCache = useRef({});

  const speak = (s) => {
    setWhisper(s);
    if (!s) return;
    const key = s.audio;
    let entry = audioCache.current[key];
    if (!entry) {
      entry = { audio: null };
      audioCache.current[key] = entry;
      fetch(`${API}/whisper/${key}`)
        .then((r) => (r.ok ? r.blob() : Promise.reject()))
        .then((blob) => {
          const a = new Audio(URL.createObjectURL(blob));
          a.volume = 0.22;
          entry.audio = a;
          a.play().catch(() => {});
        })
        .catch(() => {});
      return;
    }
    if (entry.audio) {
      entry.audio.currentTime = 0;
      entry.audio.play().catch(() => {});
    }
  };

  const hush = () => {
    setWhisper(null);
    Object.values(audioCache.current).forEach((e) => e.audio && e.audio.pause());
  };

  return (
    <section id="town" className="py-24 lg:py-32 bg-[#190A0B] relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div>
            <p className="font-mono text-xs tracking-[0.3em] uppercase text-gold mb-5">Our Home</p>
            <h2 className="font-serif font-medium text-4xl sm:text-5xl text-[#F2E5CF] tracking-tight leading-[1.08] max-w-xl">
              The Town of St. Bellmore
            </h2>
          </div>
          <p className="font-display text-sm text-[#94756A] max-w-xs leading-relaxed lg:text-right">
            An heirloom survey, redrawn from the 1887 original.
          </p>
        </Reveal>

        <Reveal>
          <div data-testid="town-map" className="relative">
            <div className="absolute -inset-2 border border-gold/40 translate-x-3 translate-y-3 pointer-events-none" aria-hidden="true" />
            <div className="relative aspect-[4/3] overflow-hidden map-vignette bg-[#d9c9a3]">
              <img
                src={IMAGES.parchment}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 w-full h-full object-cover opacity-90 sepia-[0.35]"
              />
              <div className="absolute inset-0 bg-[#e6d7b4]/55 mix-blend-multiply" />
              <MapInk onWhisper={(s) => (s ? speak(s) : hush())} />

              {/* title cartouche */}
              <div className="absolute left-[4%] top-[5%] border border-[#3a2f23]/70 bg-[#EDE3CB]/85 px-3 py-2 sm:px-5 sm:py-3 shadow-sm">
                <p className="font-serif italic font-semibold text-[#3a2f23] text-sm sm:text-xl leading-none tracking-wide">St. Bellmore</p>
                <p className="font-mono text-[7px] sm:text-[9px] tracking-[0.28em] text-[#3a2f23]/80 mt-1 uppercase">Surveyed Anno 1887</p>
              </div>

              {/* tombstone whisper */}
              <AnimatePresence>
                {whisper && (
                  <motion.div
                    key={whisper.name}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.35 }}
                    className="absolute z-20 pointer-events-none -translate-x-1/2 -translate-y-full"
                    style={{ left: `${whisper.x}%`, top: `${(whisper.y / 75) * 100}%` }}
                    data-testid="tombstone-whisper"
                  >
                    <div className="mb-2 border border-[#3a2f23]/60 bg-[#EDE3CB]/95 px-3 py-2 shadow-md text-center whitespace-nowrap">
                      <p className="font-serif italic text-[#3a2f23] text-xs sm:text-sm leading-none">{whisper.name}</p>
                      <p className="font-mono text-[8px] sm:text-[9px] tracking-[0.25em] text-[#3a2f23]/75 mt-1">{whisper.dates}</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {locations.map((loc) => {
                const Icon = ICONS[loc.icon] || Landmark;
                return (
                  <div
                    key={loc.id}
                    data-testid={`map-pin-${loc.id}`}
                    className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center"
                    style={{ left: `${loc.coords.x}%`, top: `${loc.coords.y}%` }}
                    aria-hidden="true"
                  >
                    <span className="relative flex items-center justify-center w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-[#7a2e2e] text-[#EDE3CB] ring-1 ring-[#3a2f23]/60 shadow-md">
                      <Icon className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={1.75} />
                    </span>
                    <span className="mt-1.5 font-serif italic text-[10px] sm:text-xs text-[#3a2f23] bg-[#EDE3CB]/85 px-1.5 py-0.5 border border-[#3a2f23]/30 leading-none whitespace-nowrap">
                      {loc.name}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <p className="mt-8 text-center font-mono text-[10px] sm:text-xs tracking-[0.3em] uppercase text-[#94756A]">
            Our public records end here. The rest are kept elsewhere.
          </p>
        </Reveal>
      </div>
    </section>
  );
};
